/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { AbReactContext } from "abreact/incubator/context/AbReactContext";
import * as Factories from "abreact/incubator/context/ConsolidatedContextComponentFactories";
/**
 * Returns a React component, with a nullable / optional React prop for each member of TContext.
 * The component will take values supplied via React props and merge those into the standard ab initio consolidated react context object.
 * The component merges the new properties with any context properties supplied by React ancestors.
 * Specifying 'null' for any context prop will clear that property from the context.
 * The members of TContext should be marked optional using ? or undefined.
 * Generally this will be used to create a provider component for a specific piece of context.
 * <code>
 *   export interface IEditManagerContext {
 *    editManager?: IEditManagerFromItem;
 *  }
 *  export let EditManagerProvider = AbContextComponentFactories.abContextProviderFactory<IEditManagerContext>("EditManagerProvider");
 * </code>
 */
export function abContextProviderFactory(displayNameForReactComponent) {
  return Factories.consolidatedContextProviderFactory(AbReactContext, displayNameForReactComponent);
}

/* There's a big problem here. For mobx based components the consumer component must be an observer to re-render reliably.
But if this is an observer non-mobx components don't re-render reliably.
See ConsolidatedContextComponentFactories
For now the HOC approach below is the recommended way to do things.
See http://confluence.abinitio.com:8090/display/AISDK/React+16+Context+API
*/
// export function abContextConsumerFactory<TContext extends Object>(displayNameForReactComponent: string)
// //: {new (props: any): React.Component<Factories.ConsolidatedContextConsumerProps<TContext>>}
// {
//   return Factories.consolidatedContextConsumerFactory<TContext>(CONTEXT_PROPERTY_NAME, displayNameForReactComponent);
// }

// export function abContextSinglePropertyConsumerFactory<TContext extends Object, TProperty>(displayNameForReactComponent: string,
//   getPropertyFromContext: (context: Partial<TContext>) => TProperty | undefined)
// {
//   return Factories.consolidatedContextSinglePropertyConsumerFactory<TContext, TProperty>(CONTEXT_PROPERTY_NAME,
//       displayNameForReactComponent, getPropertyFromContext);
// }

/**
 * For consuming context, this creates a function that will wrap a React Component and return a new component that
 * connects to React context, and then renders the wrapped component with additional injected properties containing the context values.
 * This places the context within the standard ab consolidated react context object
 * @param displayNameForReactComponent The React Chrome debugger will display this name for the wrapper component
 * @param getInjectedPropsFromConsolidatedContext A function that takes the full consolidated context, with all possible
 * context properties that have been consolidated into a single react context, and returns the set of injected properties that will
 * be supplied to the wrapped React component. Typically this is a very dumb and seemingly redundant piece of code, although it
 * could potentially remap names, although that might be confusing.
 * ({contextProp}) => {contextProp}, using object destructuring and default object literal property names
 */
export function abContextHocFactory(displayNameForReactComponent, getInjectedPropsFromConsolidatedContext) {
  return Factories.consolidatedContextHocFactory(AbReactContext, displayNameForReactComponent, getInjectedPropsFromConsolidatedContext);
}
export function abContextHocFactoryFC(displayNameForReactComponent, getInjectedPropsFromConsolidatedContext) {
  return Factories.consolidatedContextHocFactoryFC(AbReactContext, displayNameForReactComponent, getInjectedPropsFromConsolidatedContext);
}

/**
 * Use this to provide complete passthrough context.
 */

export let AbConsolidatedContextProvider = Factories.contextProviderFactory(AbReactContext, "AbConsolidatedContextProvider");
let hocFunc = Factories.contextHocFactory(AbReactContext, "withAbConsolidatedContext");
export function withAbConsolidatedContext(base) {
  return hocFunc(base);
}
//# sourceMappingURL=AbContextComponentFactories.js.map
